/**
 * View state (selected node, filters, last screen, saved filter presets) is
 * stored in localStorage. A single shared key leaks the previous session to
 * whoever signs in next on the same browser, so every entry is scoped to a
 * user and an organization.
 *
 * Each organization keeps its own snapshot, so switching back restores it.
 * Sign-out deletes every scoped entry. Signing in also drops entries that
 * belong to anyone else, in case the previous session ended without a
 * sign-out.
 */

export interface ViewStateScope {
	userId: string;
	organizationId: string;
}

export const LEGACY_VIEW_STATE_KEY = "relatance-view-state";
export const LEGACY_FILTER_PRESETS_KEY = "relatance-filter-presets";

const VIEW_STATE_PREFIX = `${LEGACY_VIEW_STATE_KEY}:`;
const FILTER_PRESETS_PREFIX = `${LEGACY_FILTER_PRESETS_KEY}:`;

export function viewStateStorageKey(scope: ViewStateScope): string {
	return `${VIEW_STATE_PREFIX}${scope.userId}:${scope.organizationId}`;
}

export function filterPresetsStorageKey(scope: ViewStateScope): string {
	return `${FILTER_PRESETS_PREFIX}${scope.userId}:${scope.organizationId}`;
}

export function isPersistedViewStateKey(key: string): boolean {
	return (
		key === LEGACY_VIEW_STATE_KEY ||
		key.startsWith(VIEW_STATE_PREFIX) ||
		key === LEGACY_FILTER_PRESETS_KEY ||
		key.startsWith(FILTER_PRESETS_PREFIX)
	);
}

function listKeys(storage: Storage): string[] {
	if (typeof storage.length !== "number" || typeof storage.key !== "function") {
		return [];
	}
	const keys: string[] = [];
	for (let index = 0; index < storage.length; index++) {
		const key = storage.key(index);
		if (key !== null) keys.push(key);
	}
	return keys;
}

function removeKeys(
	storage: Storage,
	predicate: (key: string) => boolean,
): void {
	try {
		for (const key of listKeys(storage)) {
			if (predicate(key)) storage.removeItem(key);
		}
	} catch (error) {
		console.error("Failed to clear persisted view state:", error);
	}
}

/** Removes entries saved by anyone other than `userId`, and the legacy keys. */
export function clearOtherUsersPersistedViewState(
	storage: Storage,
	userId: string,
): void {
	const viewPrefix = `${VIEW_STATE_PREFIX}${userId}:`;
	const presetPrefix = `${FILTER_PRESETS_PREFIX}${userId}:`;
	removeKeys(storage, (key) => {
		if (!isPersistedViewStateKey(key)) return false;
		return !(key.startsWith(viewPrefix) || key.startsWith(presetPrefix));
	});
}

/** Sign-out: nothing view-related remains for the next person on this browser. */
export function clearAllPersistedViewState(storage: Storage): void {
	removeKeys(storage, isPersistedViewStateKey);
}

let persistenceEnded = false;

/**
 * Clears everything and blocks further writes until the page unloads. For
 * callers about to navigate away: a render committed between the clear and
 * the unload would otherwise save the snapshot straight back. If the
 * navigation never happens, the page stays unsaved until it is reloaded.
 */
export function endPersistedViewState(storage: Storage): void {
	persistenceEnded = true;
	clearAllPersistedViewState(storage);
}

export function readPersistedJson(storage: Storage, key: string): unknown {
	try {
		const raw = storage.getItem(key);
		if (!raw) return null;
		return JSON.parse(raw) as unknown;
	} catch (error) {
		console.error("Failed to load view state from storage:", error);
		return null;
	}
}

export function writePersistedJson(
	storage: Storage,
	key: string,
	value: unknown,
): void {
	if (persistenceEnded) return;
	try {
		storage.setItem(key, JSON.stringify(value));
	} catch (error) {
		console.error("Failed to save view state to storage:", error);
	}
}
